// Unpublished Work © 2023 Deere & Company.
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { Button, Grid, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import "./styles.scss";

export type SummaryItem = {
  title: string;
  value?: string | null;
  component?: JSX.Element;
  isLink?: boolean;
  onClick?: any;
};

interface SummarySectionProps {
  summary: SummaryItem[];
  expandFinal?: boolean;
  children?: ReactNode;
  maxNumOfColumns?: number;
}

const SummarySection = ({
  summary,
  expandFinal,
  children,
  maxNumOfColumns = 6,
}: SummarySectionProps) => {
  const { t } = useTranslation("common");
  const theme = useTheme();
  const gridItemsWidth =
    summary.length > maxNumOfColumns ? maxNumOfColumns : summary.length;

  const summaryContainerStyles = {
    backgroundColor: theme.tokens.FuelColorGray50,
    padding: "16px",
  };

  const summaryContainerWithChildrenStyles = {
    ...summaryContainerStyles,
    backgroundColor: theme.tokens.FuelColorWhite,
    padding: "8px 16px 12px 16px",
    borderRadius: "8px",
    boxShadow: "none",
    outline: "1px solid",
    outlineColor: theme.tokens.FuelColorBorderLight,
  };

  const containerStyles = children
    ? summaryContainerWithChildrenStyles
    : summaryContainerStyles;

  return (
    <Grid container sx={containerStyles} data-testid="header-section">
      <Grid size={12}>
        <Typography variant="h4">{t("summary.label")}</Typography>
      </Grid>
      <Grid
        size={12}
        sx={{
          borderBottom: children ? "1px solid" : "none",
          borderColor: theme.tokens.FuelColorBorderLight,
          marginBottom: children ? "12px" : "0",
        }}
      >
        <dl
          className="header-details"
          style={{
            gridTemplateColumns: `repeat(${gridItemsWidth}, 1fr)`,
          }}
        >
          {summary.map((summaryItem, index, { length }) => {
            // If expandFinal prop is true, expand the last grid item to fill the last row
            const fillLastRow =
              expandFinal && index + 1 === length
                ? {
                    style: {
                      gridColumn: `span ${
                        gridItemsWidth - (index % gridItemsWidth)
                      }`,
                    },
                  }
                : {};

            const dataTestId = `summary-${summaryItem.title
              .split(" ")
              .map(
                (s) => `${s.charAt(0).toUpperCase()}${s.substring(1, s.length)}`
              )
              .join("")}`;

            const summaryValue = summaryItem.component
              ? summaryItem.component
              : summaryItem.value;

            return (
              <div key={`summary-${summaryItem.title}`} {...fillLastRow}>
                <dt style={{ color: theme.tokens.FuelColorTextTertiary }}>
                  {summaryItem.title}
                </dt>
                <dd
                  style={{
                    color: theme.tokens.FuelColorTextPrimary,
                    minHeight: "21px",
                  }}
                  data-testid={dataTestId}
                >
                  {summaryItem.isLink ? (
                    <Button
                      data-testid={`${dataTestId}-link`}
                      id="idLink"
                      onClick={summaryItem.onClick}
                      style={{ height: 0, fontWeight: "inherit" }}
                      variant="text-inline"
                      endIcon={<OpenInNewIcon data-testid="open-in-new-icon" />}
                    >
                      {summaryValue}
                    </Button>
                  ) : (
                    summaryValue
                  )}
                </dd>
              </div>
            );
          })}
        </dl>
      </Grid>
      {children}
    </Grid>
  );
};

export default SummarySection;
